import cn from 'classnames';
import React from 'react';
import {useSelector} from 'react-redux';

import {AppButton} from '@/components/Button';
import RemoteIconV2 from '@/components/Icon/RemoteIconV2';
import PopupStickedWithSwipeClose from '@/components/Popup/PopupStickedWithSwipeClose';
import DevicesSettingsSection from '@/containers/DevicesPage/components/DevicesSettingsSection/DevicesSettingsSection';
import MyDevicesManageDevice from '@/containers/DevicesPage/components/MyDevicesView/components/MyDevicesManageDevice';
import useOnConnectClick from '@/containers/DevicesPage/hooks/useOnConnectClick';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import routerService from '@/services/route/routerService';
import {selectCurrentProductInfo, selectIsSelectedDeviceConnectedToIot} from '@/state/selectors/device';
import {twx} from '@/utils/tailwindUtils';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners} from '@/views/WarningBanners';

const DeviceSettingLayout = ({children, footer}) => {
    const isMobile = useIsMobileLayout();

    if (isMobile)
        return (
            <>
                <MyDevicesManageDevice />
                <PopupStickedWithSwipeClose
                    onClose={routerService.goBack}
                    className='flex flex-col items-center px-8 ml:max-w-[568px] ml:pb-8'
                    isCloseVisible
                    isSwipeDashVisible={false}
                >
                    {children}
                    {footer}
                </PopupStickedWithSwipeClose>
            </>
        );

    return (
        <DevicesSettingsSection.Container className='flex h-full w-full flex-col'>
            {children}
            <div className='mt-auto'>
                {footer}
                <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.DEVICES} contentClassName='ml:px-8' />
            </div>
        </DevicesSettingsSection.Container>
    );
};

const ContentHeader = ({icon, title, description}) => {
    return (
        <div className='flex flex-col items-center gap-2 text-center ml:mt-8'>
            <RemoteIconV2 className='h-16 w-16' icon={icon} />
            <h1 className='text-28 font-bold ml:text-32'>{title}</h1>
            <p className='text-16'>{description}</p>
        </div>
    );
};

const Header = ({icon, title, description}) => {
    return (
        <div className='flex flex-col items-center gap-2 ml:mt-8 ml:items-start'>
            <RemoteIconV2 className='h-16 w-16 ml:hidden' icon={icon} />
            <h1 className='text-28 font-bold ml:text-32'>{title}</h1>
            <p className='text-16'>{description}</p>
        </div>
    );
};

const ControlsContainer = ({children, className}) => {
    return <div className={twx('mt-8 w-full', className)}>{children}</div>;
};

const SwitchContainer = ({children, className}) => {
    return <div className={twx('mt-8 w-full rounded-xl bg-primary-dark-5', className)}>{children}</div>;
};

const SaveButton = ({className, text, alt, onClick, isDisabled, onDisableClick}) => {
    return (
        <AppButton
            className={cn(className, 'w-full')}
            primary
            light
            onClick={onClick}
            alt={alt}
            disabled={isDisabled}
            onDisableClick={onDisableClick}
        >
            {text}
        </AppButton>
    );
};

const ConnectButton = ({className, text, alt}) => {
    const device = useSelector(selectCurrentProductInfo);
    const {type, deviceSerialNumber} = device;
    const handleOnConnectClick = useOnConnectClick(type, deviceSerialNumber);

    return (
        <AppButton className={cn(className, 'w-full')} primary light onClick={handleOnConnectClick} alt={alt}>
            {text}
        </AppButton>
    );
};

const ConnectButtonContainer = ({
    className,
    connectButton,
    primaryButton,
    onPrimaryButtonClick,
    isDisabled,
    onDisableClick,
}) => {
    const isSelectedDeviceConnectedToIot = useSelector(selectIsSelectedDeviceConnectedToIot);

    return isSelectedDeviceConnectedToIot ? (
        <SaveButton
            className={className}
            text={primaryButton?.text}
            aria-label={primaryButton?.alt}
            onClick={onPrimaryButtonClick}
            isDisabled={isDisabled}
            onDisableClick={onDisableClick}
        />
    ) : (
        <ConnectButton className={className} text={connectButton?.text} aria-label={connectButton?.alt} />
    );
};

DeviceSettingLayout.Header = Header;
DeviceSettingLayout.ContentHeader = ContentHeader;
DeviceSettingLayout.ControlsContainer = ControlsContainer;
DeviceSettingLayout.SwitchContainer = SwitchContainer;
DeviceSettingLayout.ConnectButtonContainer = ConnectButtonContainer;

export default DeviceSettingLayout;
